import { localInputToIso } from '../../utils/eventDateTime';
import { draftStartsAtIso, type EventDraft } from './eventDraft';
import { isAnnouncementKind } from './eventKind';

// Mirrors the server's zod rules field-for-field, so an admin sees an inline
// error instead of a 400 on submit. Keep in step with `events.validator.ts`.

// Mirrors durationHoursSchema in events.validator.ts: quarter-hour grid, 0 < h <= 24.
const DURATION_STEP = 0.25;
const MAX_DURATION_HOURS = 24;

export interface EventValidationOptions {
    isCreate: boolean;
    knownThemePresets: readonly string[];
}

function isZoomUrl(raw: string): boolean {
    try {
        const url = new URL(raw);
        return url.protocol === 'https:' && url.hostname.endsWith('.zoom.us');
    } catch {
        return false;
    }
}

/** Field name -> error message. Empty object means the draft is valid. */
export function validateEventDraft(
    draft: EventDraft,
    { isCreate, knownThemePresets }: EventValidationOptions
): Record<string, string> {
    const errors: Record<string, string> = {};

    const isAnnouncement = isAnnouncementKind(draft.kind);

    if (!draft.title.trim()) errors.title = 'Required.';
    if (!draft.subtitle.trim()) errors.subtitle = 'Required.';
    // Read only by the meeting modal and the confirmation email, neither of
    // which an announcement has.
    if (!isAnnouncement) {
        if (!draft.topic.trim()) errors.topic = 'Required.';
        if (!draft.badgeLabel.trim()) errors.badgeLabel = 'Required.';
        if (!draft.actionLabel.trim()) errors.actionLabel = 'Required.';
    }

    const showFrom = localInputToIso(draft.showFromLocal);
    const authoredStartsAt = localInputToIso(draft.startsAtLocal);
    // The value actually sent, so this check matches the server's on the byte.
    const startsAt = authoredStartsAt ? draftStartsAtIso(draft) : null;

    if (!showFrom) errors.showFromLocal = 'Required.';
    if (!authoredStartsAt) errors.startsAtLocal = 'Required.';

    if (startsAt && showFrom && new Date(startsAt).getTime() < new Date(showFrom).getTime()) {
        // The announcement window is shifted back by its forced duration, so the
        // real rule an admin hits is a 15-minute floor, not "after".
        errors.startsAtLocal = isAnnouncement
            ? 'Must be at least 15 minutes after "Show from".'
            : 'Must not be before "Show from".';
    }

    // An announcement's duration is forced rather than typed (see `kindFields`).
    if (!isAnnouncement) {
        const duration = Number(draft.durationHours);
        if (!draft.durationHours.trim()) {
            errors.durationHours = 'Required.';
        } else if (!Number.isFinite(duration) || duration <= 0) {
            errors.durationHours = 'Must be a positive number of hours.';
        } else if (duration > MAX_DURATION_HOURS) {
            errors.durationHours = `At most ${MAX_DURATION_HOURS} hours.`;
        } else if (!Number.isInteger(duration / DURATION_STEP)) {
            errors.durationHours = 'Use quarter-hour steps (0.25, 0.5, 1, 1.5 …).';
        }

        if (isCreate && !draft.zoomUrl.trim()) {
            errors.zoomUrl = 'Required.';
        }
        if (draft.zoomUrl.trim() && !isZoomUrl(draft.zoomUrl.trim())) {
            errors.zoomUrl = 'Must be an https:// URL on a *.zoom.us domain.';
        }
    }

    if (!knownThemePresets.includes(draft.themePreset)) {
        errors.themePreset = 'Unknown theme preset.';
    }

    // The server cannot catch this: an empty array is a legitimate payload
    // meaning "every practice", so "narrowed to nobody" only exists as intent,
    // and only here. Without it, picking Selected and adding nothing would
    // publish to everyone - the exact opposite of what was asked for.
    if (draft.audienceMode === 'selected' && draft.practices.length === 0) {
        errors.practices = 'Add at least one practice, or switch to All practices.';
    }

    return errors;
}
